import { Link } from "react-router-dom";
import { Palette, Sparkles, Type, Quote, ArrowRight, Star, Zap } from "lucide-react";
import { Button } from "../components/ui/button";

const FreeTools = () => {
  const tools = [
    {
      id: "name-coloring-generator",
      title: "Name Coloring Page Generator",
      description: "Create personalized coloring pages with any name. Perfect for kids' birthdays, classroom activities, or custom gifts!",
      icon: Type,
      color: "violet",
      gradient: "from-violet-500 to-purple-600",
      popular: true,
      features: ["Any name", "Multiple styles", "Instant download", "Print-ready PDF"]
    },
    {
      id: "bubble-letter-generator",
      title: "Bubble Letter Generator",
      description: "Transform any text into fun bubble letters for coloring. Great for learning the alphabet, party decorations, or crafts!",
      icon: Sparkles,
      color: "pink",
      gradient: "from-pink-500 to-rose-600",
      popular: true,
      features: ["A-Z letters", "Custom words", "Kid-friendly", "Educational"]
    },
    {
      id: "quote-coloring-generator",
      title: "Quote Coloring Page Generator",
      description: "Turn inspiring quotes into beautiful coloring pages. Perfect for stress relief, mindfulness, or motivational decor!",
      icon: Quote,
      color: "blue",
      gradient: "from-blue-500 to-indigo-600",
      popular: false,
      features: ["Custom quotes", "Decorative borders", "Adult-friendly", "Inspirational"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-6">
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 font-medium hidden md:block">
                Pricing
              </Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium hidden md:block">
                Blog
              </Link>
              <Link to="/free-tools" className="text-violet-600 font-medium">
                Free Tools
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
                data-testid="nav-signup-btn"
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-100 to-pink-100 rounded-full mb-6">
            <Zap className="w-4 h-4 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">100% Free • No Signup Required</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Free Coloring Page{" "}
            <span className="bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">
              Generators
            </span>
          </h1>
          
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            Create custom coloring pages instantly with our free AI-powered tools. 
            Perfect for parents, teachers, and craft enthusiasts!
          </p>

          <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span>No account needed</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Zap className="w-4 h-4 text-violet-500" />
              <span>Instant generation</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Palette className="w-4 h-4 text-pink-500" />
              <span>Print-ready quality</span>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool) => (
              <Link
                key={tool.id}
                to={`/free-tools/${tool.id}`}
                className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-violet-200 transition-all duration-300"
                data-testid={`tool-card-${tool.id}`}
              >
                {/* Card Header */}
                <div className={`h-32 bg-gradient-to-br ${tool.gradient} p-6 relative overflow-hidden`}>
                  {tool.popular && (
                    <span className="absolute top-3 right-3 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                      Popular
                    </span>
                  )}
                  <tool.icon className="w-12 h-12 text-white/90" />
                  <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white/5 rounded-full" />
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-violet-600 transition-colors">
                    {tool.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {tool.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {tool.features.slice(0, 3).map((feature, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="flex items-center gap-2 text-violet-600 font-medium text-sm group-hover:gap-3 transition-all">
                    <span>Try Free Now</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* More Tools Coming */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-slate-50 rounded-2xl border border-slate-200">
              <Sparkles className="w-5 h-5 text-violet-500" />
              <span className="text-slate-600">
                More free tools coming soon! 
                <Link to="/signup" className="text-violet-600 font-medium ml-1 hover:underline">
                  Sign up to get notified
                </Link>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gradient-to-r from-violet-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Want Unlimited Coloring Pages?
          </h2>
          <p className="text-violet-100 text-lg mb-8 max-w-2xl mx-auto">
            Create any coloring page you can imagine with ColorWonder. 
            Generate unlimited custom pages with our full AI-powered generator.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button
              onClick={() => window.location.href = "/signup"}
              className="h-14 px-8 rounded-full bg-white text-violet-600 font-semibold text-lg hover:bg-violet-50"
              data-testid="cta-signup-btn"
            >
              Start Free - 10 Pages/Month
            </Button>
            <Link to="/pricing" className="text-white font-medium hover:underline">
              View all plans →
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© 2025 ColorWonder. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-700">Terms</Link>
            <Link to="/cookies" className="hover:text-slate-700">Cookies</Link>
            <Link to="/contact" className="hover:text-slate-700">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FreeTools;
